import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import { ButtonRegular } from '../../../components/common/buttons/ButtonRegular';
import { FormInput } from '../../../components/common/forms/FormsInput';
import { NAVIGATE_PATH } from '../../../helpers/constants';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { impersonate, isImpersonationAllowed } from '../../../requests';
import { fetchUser } from '../../../store/reducers/actionCreators';

const Impersonate = () => {
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const { user, isLoading } = useAppSelector((state) => state.userReducer);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchUser());
  }, []);

  const { data: isAllowed, isLoading: isChecking } = useQuery({
    enabled: !!user,
    queryKey: ['isImpersonationAllowed'],
    queryFn: isImpersonationAllowed,
    retry: false,
  });

  const { mutate: impersonateUser } = useMutation({
    mutationFn: impersonate,
    onSuccess: (response) => {
      if (response.accessToken) {
        localStorage.setItem('token', response.accessToken);
        localStorage.setItem('impersonated', 'true');
      }
      window.location.href = '/';
    },
    onError: (error: any) => {
      console.log('Error updating SAML config:', error);
      setErrorMessage(
        error.response?.data?.message ??
          'Failed to impersonate the user. Please try again.',
      );
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    const requestedEmail = email?.trim().toLocaleLowerCase();
    if (!requestedEmail) {
      setErrorMessage('Email is required.');
      return;
    }

    if (requestedEmail === user?.email) {
      setErrorMessage(`You are already logged in as ${requestedEmail}.`);
      return;
    }

    impersonateUser(requestedEmail);
  };

  if (isLoading) {
    return null;
  }

  if (!user) {
    return <Navigate to={NAVIGATE_PATH.login} replace={true} />;
  }

  if (isChecking) {
    return (
      <div className="flex items-center justify-center w-screen h-screen bg-white">
        <p>Checking permissions...</p>
      </div>
    );
  }

  if (!isAllowed?.allowed) {
    return (
      <div className="flex flex-col items-center justify-center w-screen h-screen bg-white">
        <p>Access Denied.</p>
        <Link
          to={NAVIGATE_PATH.upcomingClasses}
          className="underline text-blue-primary"
        >
          Go to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center w-screen h-screen bg-white">
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <div className="flex items-center justify-center gap-4">
          <FormInput
            type="email"
            placeholder="Enter user email to impersonate"
            className="flex items-center h-12 w-80"
            required={true}
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(e.target.value)
            }
          />
          <ButtonRegular
            text="Impersonate"
            className="h-12 button-primary"
            type="submit"
          />
        </div>

        {errorMessage && (
          <p className="mt-2 text-sm font-bold text-red-700">{errorMessage}</p>
        )}
      </form>
    </div>
  );
};

export default Impersonate;
